import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output, QueryList, ViewChildren, inject } from '@angular/core';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CheckboxComponent } from 'src/app/shared/components/ui/checkbox/checkbox.component';
import { SelectComponent, SelectOption } from 'src/app/shared/components/ui/select/select.component';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { MarkdownModule } from 'ngx-markdown';
import { Observable, Subscription, debounceTime, distinctUntilChanged, merge } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { LoadingButtonComponent } from 'src/app/shared/components/ui/loading-button/loading-button.component';
import { AppPopContentComponent } from 'src/app/shared/services/app-pop-content/app-pop-content.component';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import * as _ from 'underscore';
import { NotEnoughCreditComponent } from '../../../shared/components/snackbar/not-enough-credit/not-enough-credit.component';
import { ChunkService } from '../chunk/chunk.service';
import { ContentService } from '../content.service';
import { SearchProcessorService } from './search-processor.service';
import { SearchResultsComponent } from './search-results/search-results.component';
import { Chunk, Edition } from './search.dto';

declare let Prism;

@Component({
  selector: 'app-searcher',
  templateUrl: './searcher.component.html',
  styleUrls: ['./searcher.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MarkdownModule,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    CheckboxComponent,
    SelectComponent,
    MatButtonModule,
    MatProgressBarModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    SearchResultsComponent,
    LoadingButtonComponent,
  ],
})
export class SearcherComponent implements OnInit, OnDestroy {
  @Input() displayResults = true;
  @Output() setQueryParams = new EventEmitter<any>();
  @Output() formValueChanged = new EventEmitter<any>();
  @Output() searchCompleted = new EventEmitter<any>();
  @Output() aiStreamUpdate = new EventEmitter<string>();

  @ViewChildren('videoPlayer') videos!: QueryList<any>;

  private formChangesSubscription: Subscription;
  private searchProcessor = inject(SearchProcessorService);

  data: any = {
    container: 'page',
  };
  chunks: Chunk[] = [];
  editions: Edition[] = [];
  language;
  searchForm: UntypedFormGroup;
  storage;
  loading = false;
  AIResponse;
  searchAIEventSource: EventSource;
  searchTypes: any[] = [
    { name: 'Search.VectorSearch', value: 'vector' },
    { name: 'Search.AISearch', value: 'ai_gemini' },
  ];
  tags: Array<{ content: string }> = [];
  searchOptions: any[] = [
    { name: 'Text', value: 'editions' },
    { name: 'Tags', value: 'tags' },
  ];

  readonly scoreOptions: SelectOption[] = [
    { label: 'Search.Score.VeryHigh', value: '0.85' },
    { label: 'Search.Score.High', value: '0.80' },
    { label: 'Search.Score.Medium', value: '0.75' },
    { label: 'Search.Score.Low', value: '0.70' },
    { label: 'Search.Score.Weak', value: '0.65' },
  ];
  finalAIResponse = '';
  highlightedChunkID = '';
  searchAI = 'gemini-3-flash-preview';
  userConfigurationSubscription = this.authService.userConfiguration$.subscribe((config) => {
    if (config.searchAI) {
      this.searchAI = config.searchAI.startsWith('gpt') ? 'gemini-3-flash-preview' : config.searchAI;
    }
    if (config.searchSettings) {
      this.setQueryParams.emit(config.searchSettings);
    }
  });
  chunkOnEditSubscription = this.chunkService.chunkEditEvent$.subscribe(async ($event: any) => {
    // Chunk edit logic logic removed for AI response
  });
  includeOutOfContext = new FormControl(false);
  includeContext = new FormControl(false);
  contextDepth = new FormControl(1);
  // Note Ibrahima: Je désactive la fonctionnalité de autocomplete, mais sans la retirer du code.
  // Au cau où on voudrait la réactiver
  throttledTagsAutocomplete = _.throttle(this.tagsAutocomplete.bind(this), 1500);
  temporaryAnswer: any = '';
  rawTemporaryAnswer = '';

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: any,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) private bottomSheetData: any,
    private translate: TranslateService,
    private _sanitizer: DomSanitizer,
    private router: Router,
    private fb: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private toast: ToastService,
    private chunkService: ChunkService,
    private authService: AuthService,
    private dialog: MatDialog,
    private contentService: ContentService
  ) {
    const data = this.dialogData || this.bottomSheetData;
    this.data = data || { container: 'page' };
    this.storage = inject(Storage);
    this.includeOutOfContext.setValue(localStorage.getItem('IncludeOutOfContext') == 'true' || false);
    this.language = localStorage.getItem('language') || this.translate.currentLang;

    const initialSearchType = localStorage.getItem('searchType') || 'vector';
    const resolvedSearchType = initialSearchType === 'text' ? 'vector' : initialSearchType;

    this.searchForm = this.fb.group({
      searchTerm: ['', Validators.required],
      searchOptions: [['editions', 'tags']],
      searchType: [resolvedSearchType, Validators.required],
      scoreOption: localStorage.getItem('minScore') || '0.89',
    });
    this.translate.onLangChange.subscribe((res) => {
      this.translateOptions();
      // Logic for AIResponseChunk translation handling removed
    });
  }
  ngOnDestroy(): void {
    this.chunkOnEditSubscription.unsubscribe();
    this.userConfigurationSubscription.unsubscribe();
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }
  }

  getDownloadURL(url) {
    const reference = ref(this.storage, url);
    return getDownloadURL(reference);
  }
  set searchType(value) {
    this.searchForm.get('searchType').setValue(value);
  }
  get searchType() {
    return this.searchForm.get('searchType').value;
  }
  set searchTerm(value) {
    this.searchForm.get('searchTerm').setValue(value);
  }
  get searchTerm() {
    return this.searchForm.get('searchTerm').value;
  }
  set sfSearchOptions(value) {
    this.searchForm.get('searchOptions').setValue(value);
  }
  get sfSearchOptions() {
    return this.searchForm.get('searchOptions').value;
  }
  set minScore(value) {
    this.searchForm.get('scoreOption').setValue(value);
  }
  get minScore() {
    return this.searchForm.get('scoreOption').value;
  }

  ngOnInit(): void {
    this.formChangesSubscription = merge(this.searchForm.valueChanges, this.includeOutOfContext.valueChanges)
      .pipe(
        debounceTime(400),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe(() => {
        const formValues = this.searchForm.getRawValue();
        const queryParams = {
          textToSearch: formValues.searchTerm,
          compoundOptions: formValues.searchOptions.join(),
          searchType: formValues.searchType,
          minScore: formValues.scoreOption,
          includeOutOfContext: this.includeOutOfContext.value,
        };
        this.formValueChanged.emit(queryParams);
      });
  }

  translateOptions() {
    this.searchOptions.map((e) => {
      e.name = this.translate.instant(`Search.Options.${e.value}`);
    });
  }

  onSearchTypeChange(event) {
    localStorage.setItem('searchType', event.value);
  }

  onWithResponseChange(checked: boolean) {
    const value = checked ? 'ai_gemini' : 'vector';
    this.searchType = value;
    localStorage.setItem('searchType', value);
  }

  onScoreOptionChange(event: any) {
    const value = event && typeof event === 'object' && 'value' in event ? event.value : event;
    localStorage.setItem('minScore', value);
  }

  navigateToCourse(courseID, sectionID, lectureID) {
    this.router.navigate([`knowledge/displayer/${courseID}/${sectionID}/${lectureID}`]);
  }

  navigateToBlog(blogID) {
    this.router.navigate([`articles/post/${blogID}`]);
  }
  isStringifiedJSON(str) {
    try {
      const data = JSON.parse(str);
      return !!data.chunks;
    } catch (e) {
      return false;
    }
  }

  async search(type) {
    const onSearchResults = async (res) => {
      let noAiAnswer = false;
      if (res.AIResponse) {
        this.finalAIResponse = res.AIResponse;
        if (this.finalAIResponse == 'null' || this.finalAIResponse == 'Null' || this.finalAIResponse == null) {
          this.finalAIResponse = this.translate.instant('Search.NoResultFound');
          noAiAnswer = true;
        }
      }

      this.loading = false;
      this.editions = [];
      if (res.AIResponse && noAiAnswer) {
        return;
      }
      if (res.notAnswered) {
        this.finalAIResponse = this.translate.instant('Search.NoResultFound');
        return;
      }

      this.editions = await this.searchProcessor.processSearchResults(res, this.language);

      if (this.data.container == 'bottom-sheet') {
        this.changeDetectorRef.markForCheck();
      }
      this.changeDetectorRef.markForCheck();
      this.searchCompleted.emit({
        editions: this.editions,
        finalAIResponse: this.finalAIResponse,
        temporaryAnswer: this.temporaryAnswer,
        searchType: this.searchType,
        loading: false,
      });
    };
    const searchTerm = this.searchTerm;
    const compoundOptions = this.sfSearchOptions.length == 0 ? 'editions' : this.sfSearchOptions.join();
    const searchType = this.searchType;
    const minScore = this.minScore;
    const queryParams = {
      textToSearch: searchTerm,
      compoundOptions,
      searchType,
      minScore,
      includeOutOfContext: this.includeOutOfContext.value,
    };
    this.setQueryParams.emit(queryParams);
    this.authService.setUserConfigurationDocument({
      searchSettings: queryParams,
    });
    if (searchTerm) {
      this.searchTerm = searchTerm;
      this.loading = true;
      let subscriptionSearch: Observable<any>;

      if (type == 'vector') {
        subscriptionSearch = this.contentService.searchVector(searchTerm, compoundOptions, minScore);
      } else if (type.includes('ai')) {
        this.finalAIResponse = '';

        const modelName = this.searchAI;
        this.searchAIEventSource = await this.contentService.searchAI(
          searchTerm,
          modelName,
          compoundOptions,
          this.includeContext.value,
          this.contextDepth.value,
          this.includeOutOfContext.value,
          minScore
        );
        this.searchAIEventSource.onmessage = (event) => {
          const language = this.translate.currentLang;
          const dataStr = event.data;
          if (this.isStringifiedJSON(dataStr)) {
            const data = JSON.parse(dataStr);
            onSearchResults(data);
            this.temporaryAnswer = '';
            this.rawTemporaryAnswer = '';
          } else {
            this.rawTemporaryAnswer += dataStr;
            this.temporaryAnswer = this.rawTemporaryAnswer;
            this.aiStreamUpdate.emit(this.temporaryAnswer);
          }
        };
        this.searchAIEventSource.onerror = (event) => {
          if ((event as any).readyState === EventSource.CLOSED) {
            // console.log("Connection was closed.");
          } else {
          }
          this.loading = false;
          this.searchAIEventSource.close();
        };
      } else {
        this.loading = false;
        return;
      }

      if (subscriptionSearch) {
        subscriptionSearch.subscribe({
          next: async (res) => {
            onSearchResults(res);
          },
          error: (err) => {
            if (err.error && err.error.msg == 'notEnoughCredits') {
              this.toast.snackbar.openFromComponent(NotEnoughCreditComponent, {
                data: {
                  message: this.translate.instant('Toastr.notEnoughCredits'),
                  linkText: this.translate.instant('Toastr.addCreditsHere'),
                  linkUrl: '/myspace/credit',
                },
                duration: 10000,
              });
            }
            this.loading = false;
          },
        });
      }
    }
  }

  playVideoAt(edition: Edition, timestamp) {
    if (edition.type == 'video') {
      this.videos.forEach((video: ElementRef) => video.nativeElement.pause());
      const video: ElementRef = this.videos.find((e) => e.nativeElement.id == edition._id);
      if (video) {
        video.nativeElement.currentTime = moment.duration(timestamp).asSeconds();
        video.nativeElement.play();
      }
    }
  }
  getLanguage() {
    return this.translate.currentLang;
  }
  tagsAutocomplete() {
    const searchTerm: string = this.searchTerm;
    if (searchTerm.length > 3) {
      this.contentService.tagsAutocomplete(searchTerm).subscribe((res: Array<{ content: string }>) => (this.tags = res));
    }
  }
  onFocus() {
    this.highlightedChunkID = '';
  }
  visulizeContent(contentID) {
    this.dialog.open(AppPopContentComponent, {
      width: '70vw',
      maxWidth: '80vw',
      data: { contentID },
    });
  }
  onOutOfContextChange(value) {
    localStorage.setItem('IncludeOutOfContext', value);
  }
}
